import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import styled from "styled-components";
import QRCode from "react-qr-code";
import { Helmet } from "react-helmet";

const Page = styled.div`
  min-height: 100vh;
  padding: 22px 18px 34px;
  background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
  color: #e5e7eb;
  font-family: Arial, sans-serif;
  direction: ${({ rtl }) => (rtl ? "rtl" : "ltr")};
  text-align: ${({ rtl }) => (rtl ? "right" : "left")};
`;

const HeroQR = styled.div`
  position: absolute;
  right: 24px;
  bottom: 24px;

  background: white;
  padding: 8px;
  border-radius: 12px;

  display: flex;
  flex-direction: column;
  align-items: center;

  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
`;

const QRText = styled.div`
  margin-top: 6px;
  font-size: 10px;
  font-weight: 700;
  color: #111827;
`;

const Card = styled.div`
  width: 100%;
  max-width: 880px;
  margin: 0 auto;
  padding: 22px;
  border-radius: 28px;
  background: rgba(0, 0, 0, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const LogoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const TopRightButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Logo = styled.img`
  width: 150px;
  height: auto;
  margin-left: -10px;
`;

const HeaderSection = styled.div`
  margin-bottom: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 6px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 34px;
  color: #fff;
  text-align: left;
`;

const SubTitle = styled.div`
  color: #94a3b8;
  font-size: 16px;
  text-align: left;
`;

const BackButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 9px 14px;
  margin-top: 12px;
  margin-bottom: 8px;

  background: rgba(255, 255, 255, 0.12);
  color: white;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  margin: 24px 0 8px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  border: none;
  border-radius: 999px;
  padding: 9px 14px;
  cursor: pointer;
  font-weight: 800;
  color: ${({ active }) => (active ? "#020617" : "#e5e7eb")};
  background: ${({ active }) => (active ? "#38bdf8" : "rgba(255,255,255,0.1)")};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 46px 68px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 14px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 560px) {
    grid-template-columns: 38px 64px 1fr;
  }
`;

const Rank = styled.div`
  font-weight: 900;
  color: #fff;
  font-size: 16px;
`;

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 999px;
  object-fit: cover;
  background: #111827;
`;

const AvatarFallback = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 999px;
  background: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 22px;
`;

const Name = styled.div`
  color: #fff;
  font-weight: 800;
`;

const Meta = styled.div`
  color: #94a3b8;
  font-size: 13px;
  margin-top: 3px;
`;

const Score = styled.div`
  color: #38bdf8;
  font-weight: 900;
  white-space: nowrap;

  @media (max-width: 560px) {
    grid-column: 3;
  }
`;

const Empty = styled.div`
  padding: 30px 0;
  text-align: center;
  color: #94a3b8;
`;

const Copyright = styled.div`
  width: 100%;
  max-width: 880px;
  margin: 14px auto 0;
  text-align: center;
  color: rgba(229, 231, 235, 0.65);
  font-size: 12px;
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 8px;
  flex-wrap: wrap;
`;

const AutoRotateControl = styled.label`
  display: flex;
  align-items: center;
  gap: 7px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
  user-select: none;
`;

const Switch = styled.input`
  width: 15px;
  height: 15px;
  cursor: pointer;
`;

const HeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SettingsButton = styled.button`
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.1);
  color: #e5e7eb;
  font-size: 18px;
  font-weight: 900;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalCard = styled.div`
  width: calc(100% - 40px);
  max-width: 350px;
  padding: 16px 18px;
  border-radius: 24px;
  background: #111827;
  border: 1px solid rgba(255, 255, 255, 0.14);
  color: #e5e7eb;
`;

const ModalTitle = styled.h3`
  margin: 0 0 10px;
  color: #fff;
  font-size: 18px;
`;

const MetricItem = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #e5e7eb;
  cursor: pointer;

  input {
    margin: 0;
  }
`;

const SettingRow = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
`;

const CloseModalButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 999px;
  padding: 9px 14px;
  margin-top: 10px;
  font-size: 15px;
  font-weight: 800;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 20px;
  margin-top: 6px;
`;

const Hero = styled.div`
  position: relative;
  height: 260px;

  margin-top: -22px;
  margin-left: -22px;
  margin-right: -22px;

  border-radius: 28px 28px 0 0;

  overflow: hidden;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.25),
    rgba(0, 0, 0, 0.65)
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  height: 100%;
  padding: 20px 24px;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const HeroTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeroBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ rtl }) => (rtl ? "flex-end" : "flex-start")};
  text-align: ${({ rtl }) => (rtl ? "right" : "left")};
`;

const HeroTitle = styled.h1`
  margin: 0;
  color: white;
  font-size: 42px;
  font-weight: 800;
`;

const HeroSubtitle = styled.div`
  color: rgba(255, 255, 255, 0.85);
  font-size: 16px;
  margin-top: 8px;
`;

const HeroStats = styled.div`
  color: white;
  margin-top: 10px;
  font-size: 14px;
`;

const Content = styled.div`
  padding: 22px;
`;

const SettingColumn = styled.div`
  padding: 8px 0;
`;

const SettingLabel = styled.div`
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 4px;
`;

const ActivitySelect = styled.select`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 14px;
  padding: 8px 12px;
  font-size: 14px;
  background: #0f172a;
  color: #e5e7eb;
  font-weight: 700;
  outline: none;
`;

function CircleLeaderboard(props) {
  const circleId = props.match?.params?.id;

  const [circleData, setCircleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("steps");
  const [leaderboard, setLeaderboard] = useState([]);
  const [stepsLeaderboard, setStepsLeaderboard] = useState([]);
  const [circleNotes, setCircleNotes] = useState([]);
  const [autoRotate, setAutoRotate] = useState(true);
  const [showQR, setShowQR] = useState(true);
  const [rtlMode, setRtlMode] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState("all");
  const [visibleMetrics, setVisibleMetrics] = useState({
    steps: true,
    distance: true,
    points: true,
    checkins: true,
  });

  const metricOrder = useMemo(() => {
    const items = [];

    if (circleData?.showSteps && visibleMetrics.steps) items.push("steps");

    if (visibleMetrics.distance) items.push("distance");

    if (visibleMetrics.points) items.push("points");

    if (visibleMetrics.checkins) items.push("checkins");

    return items;
  }, [circleData?.showSteps, visibleMetrics]);

  const circleActivities = useMemo(() => {
    const goals = Array.isArray(circleData?.goals) ? circleData.goals : [];

    return goals
      .map((goal) => ({
        id: String(goal.goal_id || goal._id || ""),
        title:
          goal.goalDetails?.goal_title ||
          goal.goal_title ||
          goal.title ||
          goal.name ||
          "Untitled Activity",
      }))
      .filter((goal) => goal.id);
  }, [circleData?.goals]);

  useEffect(() => {
    loadCircle();

    const interval = setInterval(() => {
      loadCircle();
    }, 120000);

    return () => clearInterval(interval);
  }, [circleId]);

  useEffect(() => {
    const savedAutoRotate = localStorage.getItem("circleLeaderboardAutoRotate");

    const savedShowQR = localStorage.getItem("circleLeaderboardShowQR");

    const savedActivity = localStorage.getItem("circleLeaderboardActivity");

    const savedMetric = localStorage.getItem("circleLeaderboardMetric");

    if (savedAutoRotate !== null) {
      setAutoRotate(savedAutoRotate === "true");
    }

    if (savedShowQR !== null) {
      setShowQR(savedShowQR === "true");
    }

    if (savedActivity) {
      setSelectedActivityId(savedActivity);
    }

    if (savedMetric) {
      setMetric(savedMetric);
    }

    const savedRtlMode = localStorage.getItem("circleLeaderboardRtlMode");

    if (savedRtlMode !== null) {
      setRtlMode(savedRtlMode === "true");
    }

    const savedVisibleMetrics = localStorage.getItem(
      "circleLeaderboardVisibleMetrics",
    );

    if (savedVisibleMetrics) {
      try {
        setVisibleMetrics({
          steps: true,
          distance: true,
          points: true,
          checkins: true,
          ...JSON.parse(savedVisibleMetrics),
        });
      } catch (e) {
        localStorage.removeItem("circleLeaderboardVisibleMetrics");
      }
    }
  }, []);

  const updateVisibleMetric = (key, checked) => {
    setVisibleMetrics((current) => {
      const next = {
        ...current,
        [key]: checked,
      };

      const hasAtLeastOneMetric =
        next.steps || next.distance || next.points || next.checkins;

      if (!hasAtLeastOneMetric) {
        return current;
      }

      return next;
    });
  };

  useEffect(() => {
    localStorage.setItem(
      "circleLeaderboardVisibleMetrics",
      JSON.stringify(visibleMetrics),
    );
  }, [visibleMetrics]);

  useEffect(() => {
    if (!metricOrder.includes(metric) && metricOrder.length) {
      setMetric(metricOrder[0]);
    }
  }, [metricOrder, metric]);

  useEffect(() => {
    localStorage.setItem("circleLeaderboardRtlMode", String(rtlMode));
  }, [rtlMode]);

  useEffect(() => {
    localStorage.setItem("circleLeaderboardAutoRotate", String(autoRotate));
  }, [autoRotate]);

  useEffect(() => {
    localStorage.setItem("circleLeaderboardShowQR", String(showQR));
  }, [showQR]);

  useEffect(() => {
    localStorage.setItem("circleLeaderboardActivity", selectedActivityId);
  }, [selectedActivityId]);

  useEffect(() => {
    localStorage.setItem("circleLeaderboardMetric", metric);
  }, [metric]);

  useEffect(() => {
    if (!autoRotate) return;
    if (!metricOrder.length) return;

    const interval = setInterval(() => {
      setMetric((currentMetric) => {
        const currentIndex = metricOrder.indexOf(currentMetric);
        const nextIndex =
          currentIndex === -1 || currentIndex === metricOrder.length - 1
            ? 0
            : currentIndex + 1;

        return metricOrder[nextIndex];
      });
    }, 300000); // 5 minutes

    return () => clearInterval(interval);
  }, [autoRotate, metricOrder]);

  const loadCircle = async () => {
    try {
      setLoading(true);

      const circleRes = await axios.post(
        process.env.REACT_APP_API_URL + "circles/pub/byid/",
        { circleId },
      );

      const circle = circleRes.data.circle;
      const baseLeaderboard = circleRes.data.leaderboard || [];

      let stepsRows = [];

      try {
        const stepsRes = await axios.post(
          process.env.REACT_APP_API_URL + "logs/steps/leaderboard",
          {
            deviceUID: "eabc2583-cd2d-4b18-bcdc-abd890f7acc3",
            range: "daily",
            limit: 200,
          },
        );

        if (stepsRes.data.success) {
          stepsRows = stepsRes.data.leaderboard || [];
        }
      } catch (stepsError) {
        console.log("Steps leaderboard load error:", stepsError);
      }

      let notesRows = [];

      try {
        const notesRes = await axios.post(
          process.env.REACT_APP_API_URL + "notes/get-recent-notes-bycircleid",
          {
            circleId,
            deviceUID: "public",
            excludeOwn: false,
            limit: 300,
            numOfDays: 30,
          },
        );

        if (notesRes.data.success) {
          notesRows = notesRes.data.notes || [];
        }
      } catch (notesError) {
        console.log("Circle notes load error:", notesError);
      }

      setCircleData(circle);
      setLeaderboard(baseLeaderboard);
      setStepsLeaderboard(stepsRows);
      setCircleNotes(notesRows);
    } catch (error) {
      console.log("Circle leaderboard load error:", error);
    } finally {
      setLoading(false);
    }
  };

  function getCircleStatsFromNotes(userId, notes = []) {
    let points = 0;
    let checkins = 0;
    let distance = 0;
    const imageBonusTracker = new Set();

    notes.forEach((note) => {
      if (
        selectedActivityId !== "all" &&
        String(
          note?.note_rel_goal_parent_id ||
            note?.note_rel_goal_id ||
            note?.goal_id ||
            note?.note_goal_id ||
            "",
        ) !== String(selectedActivityId)
      ) {
        return;
      }
      if (String(note?.note_type) !== "2") return;
      if (String(note?.created_uid) !== String(userId)) return;

      checkins += 1;

      const task = Array.isArray(note?.note_task) ? note.note_task[0] : null;
      const taskTotalScore = Number(task?.task_totalscore || 0);

      if (!Number.isNaN(taskTotalScore)) {
        points += Math.round(taskTotalScore * 100);
      }

      const dayKey = note?.created_date
        ? new Date(note.created_date).toISOString().slice(0, 10)
        : "unknown-day";

      const bonusKey = `${userId}_${note?.note_rel_task_id}_${dayKey}`;

      if (note?.note_image && !imageBonusTracker.has(bonusKey)) {
        points += 5;
        imageBonusTracker.add(bonusKey);
      }

      try {
        const parsed =
          typeof note?.note_value === "string"
            ? JSON.parse(note.note_value)
            : note?.note_value || {};

        const noteDistance = Number(parsed?.distance || 0);

        if (!Number.isNaN(noteDistance)) {
          distance += noteDistance;
        }
      } catch (e) {}
    });

    return {
      points,
      checkins,
      distance: Number(distance.toFixed(2)),
    };
  }

  const leaders = useMemo(() => {
    const members = leaderboard || [];

    return [...members]
      .map((member) => {
        const nickname = member.nickname || member.name || "Member";

        return {
          id: member.uid || member._id || nickname,
          nickname,
          image: member.image
            ? member.image.startsWith("http")
              ? member.image
              : process.env.REACT_APP_IMG_FOLDER + member.image
            : null,
          steps: (() => {
            const stepRow = stepsLeaderboard.find((row) => {
              const rowUserId =
                row.userId ||
                row.uid ||
                row._id ||
                row.user_id ||
                row.created_uid;

              return String(rowUserId) === String(member.uid);
            });

            return Number(
              stepRow?.steps || member.totalSteps || member.steps || 0,
            );
          })(),
          ...getCircleStatsFromNotes(member.uid, circleNotes),
        };
      })
      .map((member) => ({
        ...member,
        score:
          metric === "distance"
            ? member.distance
            : metric === "steps"
              ? member.steps
              : metric === "points"
                ? member.points
                : member.checkins,
      }))
      .filter((member) => Number(member.score) > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 30);
  }, [leaderboard, stepsLeaderboard, circleNotes, metric, selectedActivityId]);

  const formatScore = (score) => {
    if (metric === "distance") return `${Number(score).toFixed(2)} km`;
    if (metric === "steps") return `${Number(score).toLocaleString()} steps`;
    if (metric === "points") return `${Number(score).toLocaleString()} pts`;
    return `${Number(score).toLocaleString()} check-ins`;
  };

  const heroImage = circleData?.circle_image
    ? process.env.REACT_APP_IMG_FOLDER + circleData.circle_image
    : "/images/circle-placeholder.jpg";

  return (
    <Page dir={rtlMode ? "rtl" : "ltr"} rtl={rtlMode}>
      <Helmet>
        <title>Circle Leaderboard | Bealyo</title>
      </Helmet>

      <Card>
        <Hero
          style={{
            backgroundImage: `url(${heroImage})`,
          }}
        >
          <HeroOverlay />

          <HeroContent>
            <HeroTopRow>
              <Logo src="/logo.png" alt="Bealyo" />

              <TopRightButtons>
                <SettingsButton
                  type="button"
                  onClick={() => setShowSettingsModal(true)}
                >
                  ⚙
                </SettingsButton>

                {/** 
                <BackButton
                  onClick={() => props.history.push(`/circle/${circleId}`)}
                >
                  Back
                </BackButton>
                */}
              </TopRightButtons>
            </HeroTopRow>

            <HeroBottom rtl={rtlMode}>
              <HeroTitle>
                {circleData?.name ||
                  (rtlMode ? "לוח מובילים" : "Circle Leaderboard")}
              </HeroTitle>

              <HeroSubtitle>
                {rtlMode
                  ? "המשתמשים המובילים בקהילה"
                  : "Top members in this circle"}
              </HeroSubtitle>
              <HeroStats>
                {leaders.length} members •{" "}
                {selectedActivityId === "all"
                  ? "All activities"
                  : circleActivities.find((a) => a.id === selectedActivityId)
                      ?.title || "Selected activity"}{" "}
                • Live Leaderboard
              </HeroStats>
            </HeroBottom>

            {showQR && (
              <HeroQR>
                <QRCode
                  value="https://www.bealyo.com"
                  size={110}
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
                <QRText>Scan to Join</QRText>
              </HeroQR>
            )}
          </HeroContent>
        </Hero>

        <TabsRow>
          <Tabs>
            {circleData?.showSteps && visibleMetrics.steps && (
              <Tab
                active={metric === "steps"}
                onClick={() => setMetric("steps")}
              >
                {rtlMode ? "צעדים" : "Steps"}
              </Tab>
            )}

            {visibleMetrics.distance && (
              <Tab
                active={metric === "distance"}
                onClick={() => setMetric("distance")}
              >
                {rtlMode ? "מרחק" : "Distance"}
              </Tab>
            )}

            {visibleMetrics.points && (
              <Tab
                active={metric === "points"}
                onClick={() => setMetric("points")}
              >
                {rtlMode ? "נקודות" : "Points"}
              </Tab>
            )}

            {visibleMetrics.checkins && (
              <Tab
                active={metric === "checkins"}
                onClick={() => setMetric("checkins")}
              >
                {rtlMode ? "ביצועים" : "Check-ins"}
              </Tab>
            )}
          </Tabs>
        </TabsRow>

        {loading && <Empty>Loading leaderboard...</Empty>}

        {!loading && !leaders.length && <Empty>No leaderboard data yet.</Empty>}

        {!loading &&
          leaders.map((member, index) => (
            <Row key={member.id}>
              <Rank>#{index + 1}</Rank>

              {member.image ? (
                <Avatar src={member.image} alt={member.nickname} />
              ) : (
                <AvatarFallback>
                  {member.nickname?.charAt(0)?.toUpperCase() || "B"}
                </AvatarFallback>
              )}

              <div style={{ textAlign: rtlMode ? "right" : "left" }}>
                <Name>
                  {rtlMode ? `${member.nickname}@` : `@${member.nickname}`}
                </Name>

                <Meta>
                  {rtlMode
                    ? index === 0
                      ? "המוביל כרגע"
                      : "חבר בקהילה"
                    : index === 0
                      ? "Current leader"
                      : "Circle member"}
                </Meta>
              </div>

              <Score>{formatScore(member.score)}</Score>
            </Row>
          ))}
      </Card>

      <Copyright>
        © Bealyo {new Date().getFullYear()}. All rights reserved.
      </Copyright>

      {showSettingsModal && (
        <ModalOverlay onClick={() => setShowSettingsModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Leaderboard Settings</ModalTitle>

            <SettingRow>
              <span>Auto switch metrics every 5 minutes</span>

              <Switch
                type="checkbox"
                checked={autoRotate}
                onChange={(e) => setAutoRotate(e.target.checked)}
              />
            </SettingRow>

            <SettingRow>
              <span>Show QR code</span>

              <Switch
                type="checkbox"
                checked={showQR}
                onChange={(e) => setShowQR(e.target.checked)}
              />
            </SettingRow>

            <SettingRow>
              <span>RTL layout</span>

              <Switch
                type="checkbox"
                checked={rtlMode}
                onChange={(e) => setRtlMode(e.target.checked)}
              />
            </SettingRow>

            <SettingColumn>
              <SettingLabel>Activity</SettingLabel>

              <ActivitySelect
                value={selectedActivityId}
                onChange={(e) => setSelectedActivityId(e.target.value)}
              >
                <option value="all">All activities in this circle</option>

                {circleActivities.map((activity) => (
                  <option key={activity.id} value={activity.id}>
                    {activity.title}
                  </option>
                ))}
              </ActivitySelect>
            </SettingColumn>

            <SettingColumn>
              <SettingLabel>Visible Metrics</SettingLabel>

              <MetricsGrid>
                <MetricItem>
                  <Switch
                    type="checkbox"
                    checked={visibleMetrics.steps}
                    onChange={(e) =>
                      updateVisibleMetric("steps", e.target.checked)
                    }
                  />
                  <span>Steps</span>
                </MetricItem>

                <MetricItem>
                  <Switch
                    type="checkbox"
                    checked={visibleMetrics.distance}
                    onChange={(e) =>
                      updateVisibleMetric("distance", e.target.checked)
                    }
                  />
                  <span>Distance</span>
                </MetricItem>

                <MetricItem>
                  <Switch
                    type="checkbox"
                    checked={visibleMetrics.points}
                    onChange={(e) =>
                      updateVisibleMetric("points", e.target.checked)
                    }
                  />
                  <span>Points</span>
                </MetricItem>

                <MetricItem>
                  <Switch
                    type="checkbox"
                    checked={visibleMetrics.checkins}
                    onChange={(e) =>
                      updateVisibleMetric("checkins", e.target.checked)
                    }
                  />
                  <span>Check-ins</span>
                </MetricItem>
              </MetricsGrid>
            </SettingColumn>

            {/**         
            <BackButton
              type="button"
              onClick={() => props.history.push(`/circle/${circleId}`)}
            >
              ← Back to Circle
            </BackButton>
            */}
            <CloseModalButton
              type="button"
              onClick={() => setShowSettingsModal(false)}
            >
              Done
            </CloseModalButton>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
}

export default CircleLeaderboard;
